/* NOT TOYS home 2026 (L2): behaviour for the nt-home-* sections, ported from preview/home-pm.html (22 Oct).
   1 hero carousel: swipe, segmented progress, pause; 6 s on the Otherworld opener, 4.5 s on every other slide;
     holds still with reduced motion, off screen, in a hidden tab and under a finger. Slide photos load one ahead.
   2 row arrows on desktop.
   3 Find your size + the size poster: ink drawings laid out at true relative size.
   No prices are written here (every price is printed by Liquid through the money filter). */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let pageLoaded = document.readyState === 'complete';
  addEventListener('load', () => { pageLoaded = true; }, { once: true });

  /* ---------- 1 hero ---------- */
  const loadImg = (im) => { if (im.dataset.src) { if (im.dataset.srcset) { im.srcset = im.dataset.srcset; im.removeAttribute('data-srcset'); } im.src = im.dataset.src; im.removeAttribute('data-src'); } };
  const shown = (im) => getComputedStyle(im).display !== 'none' && getComputedStyle(im.parentElement).display !== 'none';
  const loadSlide = (sl) => sl && sl.querySelectorAll('img[data-src]').forEach((im) => { if (shown(im)) loadImg(im); });

  const initHero = (hero) => {
    if (!hero || hero._nt) return; hero._nt = 1;
    const track = hero.querySelector('.h-track'), slides = [...track.children], segs = [...hero.querySelectorAll('.h-segs button')], pb = hero.querySelector('.h-pause');
    if (!slides.length || segs.length !== slides.length) return;
    let cur = 0, playing = !reduce, lock = 0;
    hero.style.setProperty('--dur', '4.5s');
    // the header reads body.over-hero (phones only), as in the preview
    const mqDesk = matchMedia('(min-width:900px)');
    const headSync = () => { const r = hero.getBoundingClientRect(); document.body.classList.toggle('over-hero', !mqDesk.matches && r.bottom > 62); };
    addEventListener('scroll', headSync, { passive: true });
    if (mqDesk.addEventListener) mqDesk.addEventListener('change', headSync);
    headSync();
    const paint = (i) => {
      segs.forEach((b, k) => { b.classList.toggle('done', k < i); b.classList.remove('on'); b.setAttribute('aria-current', String(k === i)); });
      void segs[i].offsetWidth; segs[i].classList.add('on');
      hero.style.setProperty('--dur', (slides[i].dataset.dur || '4.5') + 's');
      hero.classList.toggle('sand', slides[i].dataset.tone === 'sand');
      slides.forEach((s, k) => { s.setAttribute('aria-hidden', String(k !== i)); s.querySelectorAll('a').forEach((a) => { a.tabIndex = k === i ? 0 : -1; }); });
      // load this slide's photo, and the next one a little before it arrives
      loadSlide(slides[i]);
      if (pageLoaded) loadSlide(slides[(i + 1) % slides.length]);
      else addEventListener('load', () => loadSlide(slides[(cur + 1) % slides.length]), { once: true });
    };
    const go = (i, smooth = true) => {
      cur = (i + slides.length) % slides.length; paint(cur);
      lock = Date.now() + 900;
      track.scrollTo({ left: cur * track.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    };
    segs.forEach((b, k) => {
      b.addEventListener('click', () => go(k));
      b.querySelector('i').addEventListener('animationend', () => { if (playing) go(cur + 1); });
    });
    let st = 0;
    track.addEventListener('scroll', () => {
      clearTimeout(st);
      st = setTimeout(() => { if (Date.now() < lock) return; const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth)); if (i !== cur && slides[i]) { cur = i; paint(cur); } }, 110);
    }, { passive: true });
    track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') { e.preventDefault(); go(cur + 1); } if (e.key === 'ArrowLeft') { e.preventDefault(); go(cur - 1); } });
    const setPlay = (p) => { playing = p; hero.classList.toggle('paused', !p); hero.classList.toggle('still', reduce && !p); pb.setAttribute('aria-label', p ? 'Pause the slideshow' : 'Play the slideshow'); };
    pb.addEventListener('click', () => { setPlay(!playing); if (playing) paint(cur); });
    // hold still while the hero is off screen or the tab is hidden
    if (window.IntersectionObserver) new IntersectionObserver(([e]) => hero.classList.toggle('off', !e.isIntersecting), { threshold: 0.25 }).observe(hero);
    document.addEventListener('visibilitychange', () => hero.classList.toggle('off', document.hidden));
    // a finger on the photo holds the slide
    track.addEventListener('pointerdown', () => hero.classList.add('off'));
    track.addEventListener('pointerup', () => hero.classList.remove('off'));
    track.addEventListener('pointercancel', () => hero.classList.remove('off'));
    addEventListener('resize', () => { track.scrollTo({ left: cur * track.clientWidth }); });
    setPlay(playing); paint(0);
  };

  /* ---------- 2 rows: desktop arrows ---------- */
  const wireArrows = (root) => root.querySelectorAll('.arw[data-for]').forEach((a) => {
    const row = document.getElementById(a.dataset.for), pv = a.querySelector('.pv'), nx = a.querySelector('.nx');
    if (!row || a._w) return; a._w = 1;
    const sync = () => { pv.disabled = row.scrollLeft < 4; nx.disabled = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4; };
    pv.addEventListener('click', () => row.scrollBy({ left: -row.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' }));
    nx.addEventListener('click', () => row.scrollBy({ left: row.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' }));
    row.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    row._sync = sync; sync();
  });

  /* ---------- 3 drawings at true relative size ---------- */
  const parse = (s) => { try { return JSON.parse(s) || []; } catch (e) { return []; } };
  const place = (el, items, gap, fitH) => {
    const W = el.clientWidth, Ht = el.clientHeight;
    if (!W || !Ht || !items.length) return;
    const maxH = Math.max(...items.map((x) => x.h));
    const k = Math.min(fitH(Ht) / maxH, (W - gap * (items.length - 1)) / items.reduce((s, x) => s + x.h * x.ar, 0));
    const total = items.reduce((s, x) => s + x.h * x.ar * k, 0) + gap * (items.length - 1);
    let x = (W - total) / 2;
    el.innerHTML = '<i class="base"></i>' + items.map((it) => {
      const w = it.h * it.ar * k, h = it.h * k;
      const html = `<img${it.ref ? ' class="ref"' : ''} src="${esc(it.f)}" alt="${esc(it.alt || '')}" loading="lazy" decoding="async" style="left:${x.toFixed(1)}px;width:${w.toFixed(1)}px;height:${h.toFixed(1)}px">`;
      x += w + gap; return html;
    }).join('');
  };
  const drawBento = (root) => root.querySelectorAll('.bento .stage').forEach((st) => {
    const W = st.clientWidth, Ht = st.clientHeight;
    if (!W || !Ht) return;
    if (st.dataset.charm) {
      const s = Math.min(Ht, W) * 0.95;
      st.innerHTML = `<img src="${esc(st.dataset.charm)}" alt="Drawing of the Fudge leather charm" loading="lazy" decoding="async" style="left:${(W - s) / 2}px;width:${s}px;height:${s}px">`;
      return;
    }
    if (!st._items) st._items = parse(st.dataset.items);
    place(st, st._items, 10, (h) => h - 2);
  });
  const drawLineup = (root) => root.querySelectorAll('[data-lineup]').forEach((el) => {
    if (!el._items) el._items = parse(el.dataset.lineup);
    place(el, el._items, 6, (h) => h);
  });

  /* ---------- start (and again when the theme editor reloads a section) ---------- */
  const init = (root = document) => {
    root.querySelectorAll('[data-nt-hero]').forEach(initHero);
    wireArrows(root); drawBento(root); drawLineup(root);
  };
  let rt;
  addEventListener('resize', () => {
    clearTimeout(rt);
    rt = setTimeout(() => { drawBento(document); drawLineup(document); document.querySelectorAll('.nt-h .xs').forEach((r) => r._sync && r._sync()); }, 140);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  // fonts change the tile sizes a little: lay the drawings out again once they are in
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { drawBento(document); drawLineup(document); });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
